import { MutableRefObject, ReactElement, useMemo } from 'react';
import {
  DataGridPro,
  DataGridProProps,
  GridApiPro,
  GridCallbackDetails,
  GridColDef,
  GridPaginationModel,
  GridRowIdGetter,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';
import { useDataGridStateStorage } from './hooks/useDataGridStateStorage';
import { DataGridContainer } from './container';
import { EmptyListReason } from 'components/common/noTableContentInfo/NoTableContentInfo';
import { useDataGridApi } from 'components/common/withDataGridApi';
import { useDataGridConfig } from './hooks/useDataGridConfig';
import { DataGridConfig } from './config/DataGridConfig';
import {
  applySavedColumnDimensions,
  getRowIdByIdField,
  orderColumnsBySavedOrder,
} from './utils';
import { sxStyles } from './styles';
import { getSearchableFilterFormProps } from './filterPanel/getSearchableFilterFormProps';
import { FilterPanelWrapper } from './filterPanelWrapper';
import { LocalStorageManager } from 'services/storage/LocalStorageManager';

/**
 * Main DataGrid component using Facade Pattern
 *
 * Simplified interface with configuration through DataGridConfig:
 * - Automatic state management (sorting, filtering, pagination)
 * - Automatic state persistence to localStorage
 * - Simplified configuration through Builder Pattern
 *
 * @template T - Row type in DataGrid (must have _id: string field for default getRowId)
 *
 * @example
 * const config = createClientDataGridConfig<UserOnView>(STORAGE_KEY, INITIAL_SORT_MODEL)
 *   .withPagination({ defaultPageSize: 50, pageSizeOptions: [50, 100, 300] })
 *   .withRowDoubleClick((params) => goToUserDetails(params.row._id))
 *   .build();
 *
 * <DataGrid
 *   data={users}
 *   columns={columns}
 *   config={config}
 *   loading={loading}
 * />
 */
export type DataGridProps<T extends GridValidRowModel> = {
  data: T[];
  columns: GridColDef<T>[];
  config: DataGridConfig<T>;
  loading?: boolean;
  emptyListReason?: EmptyListReason;
  styles?: typeof sxStyles;
  paginationModel?: GridPaginationModel;
  onPaginationModelChange?: (
    model: GridPaginationModel,
    details: GridCallbackDetails
  ) => void;
  rowCount?: number;
};

/**
 * Main DataGrid component
 *
 * @template T - Row type in DataGrid
 */
export function DataGrid<T extends GridValidRowModel>(
  props: DataGridProps<T>
): ReactElement {
  const {
    data,
    columns,
    config,
    loading = false,
    emptyListReason,
    styles = sxStyles,
    paginationModel: paginationModelProp,
    onPaginationModelChange: onPaginationModelChangeProp,
    rowCount,
  } = props;

  const apiRef = useDataGridApi();
  const gridConfig = useDataGridConfig(config);
  const savedGridState = useMemo(
    () =>
      LocalStorageManager.getDataGridState(
        gridConfig.dataGridInstanceName
      ),
    [gridConfig.dataGridInstanceName]
  );

  const shouldHideFooter = !loading && data.length === 0;
  const getRowId: GridRowIdGetter<T> = config.getRowId
    ? (config.getRowId as GridRowIdGetter<T>)
    : (getRowIdByIdField as GridRowIdGetter<T>);

  const validatedApiRef = apiRef as MutableRefObject<GridApiPro>;
  const orderedColumns = useMemo(() => {
    const savedOrderedFields = savedGridState?.columns?.orderedFields;

    return orderColumnsBySavedOrder(columns, savedOrderedFields);
  }, [columns, savedGridState?.columns?.orderedFields]);

  const columnsWithSavedDimensions = useMemo(
    () =>
      applySavedColumnDimensions(
        orderedColumns,
        savedGridState?.columns?.dimensions
      ),
    [orderedColumns, savedGridState?.columns?.dimensions]
  );

  let autosizeOptions: DataGridProProps['autosizeOptions'] | undefined;
  const shouldUseAutosizeFromConfig =
    typeof window !== 'undefined' &&
    Boolean(gridConfig.dataGridInstanceName) &&
    Boolean(gridConfig.autosizeOptions);

  if (shouldUseAutosizeFromConfig) {
    const hasColumnDimensions = Boolean(
      savedGridState?.columns?.dimensions &&
        Object.keys(savedGridState.columns.dimensions).length > 0
    );

    if (!hasColumnDimensions) {
      const columnsToAutosize = columnsWithSavedDimensions
        .filter((col) => col.width === undefined)
        .map((col) => col.field);

      autosizeOptions = {
        ...gridConfig.autosizeOptions,
        columns:
          columnsToAutosize.length > 0 ? columnsToAutosize : undefined,
      };
    }
  }

  useDataGridStateStorage(
    validatedApiRef,
    gridConfig.dataGridInstanceName,
    autosizeOptions,
    gridConfig.autosizeOnMount
  );

  const mergedSlotProps = useMemo<DataGridProProps['slotProps']>(() => {
    const baseSlotProps = gridConfig.slotProps ?? {};

    const baseFilterPanel = baseSlotProps.filterPanel ?? {};

    return {
      ...baseSlotProps,
      filterPanel: {
        ...baseFilterPanel,
        dataGridInstanceName: gridConfig.dataGridInstanceName,
        filterFormProps: {
          ...getSearchableFilterFormProps(),
          ...(baseFilterPanel.filterFormProps ?? {}),
        },
      },
    } as DataGridProProps['slotProps'];
  }, [gridConfig.dataGridInstanceName, gridConfig.slotProps]);

  return (
    <DataGridContainer>
      <DataGridPro
        apiRef={validatedApiRef}
        rows={data}
        columns={columnsWithSavedDimensions}
        loading={loading}
        autosizeOptions={autosizeOptions}
        getRowId={getRowId}
        sortingMode={gridConfig.sortingMode as 'client' | 'server'}
        filterMode={gridConfig.filterMode as 'client' | 'server'}
        sortModel={gridConfig.sortModel}
        onSortModelChange={gridConfig.onSortModelChange}
        filterModel={gridConfig.filterModel}
        onFilterModelChange={gridConfig.onFilterModelChange}
        pagination={!gridConfig.disablePagination}
        pageSizeOptions={gridConfig.pageSizeOptions}
        initialState={gridConfig.initialState}
        {...(paginationModelProp !== undefined && {
          paginationModel: paginationModelProp,
        })}
        {...(onPaginationModelChangeProp !== undefined && {
          onPaginationModelChange: onPaginationModelChangeProp,
        })}
        {...(rowCount !== undefined && { rowCount, paginationMode: 'server' })}
        sx={styles.table}
        onRowDoubleClick={gridConfig.onRowDoubleClick}
        showToolbar={Boolean((gridConfig as DataGridConfig<T>).slots?.toolbar)}
        slots={{
          noRowsOverlay: gridConfig.noRowsOverlay,
          noResultsOverlay: gridConfig.noRowsOverlay,
          filterPanel: FilterPanelWrapper,
          ...((gridConfig as DataGridConfig<T>).slots?.toolbar && {
            toolbar: (gridConfig as DataGridConfig<T>).slots!.toolbar,
          }),
        }}
        slotProps={mergedSlotProps}
        columnGroupingModel={gridConfig.columnGroupingModel}
        getRowClassName={gridConfig.getRowClassName}
        checkboxSelection={gridConfig.checkboxSelection}
        disableRowSelectionOnClick={gridConfig.disableRowSelectionOnClick}
        disableMultipleRowSelection={
          gridConfig.disableMultipleRowSelection
        }
        hideFooterSelectedRowCount={
          gridConfig.hideFooterSelectedRowCount ?? true
        }
        {...(gridConfig.rowSelectionModel !== undefined && {
          rowSelectionModel: gridConfig.rowSelectionModel,
        })}
        {...(gridConfig.onRowSelectionModelChange !== undefined && {
          onRowSelectionModelChange: gridConfig.onRowSelectionModelChange,
        })}
        hideFooter={shouldHideFooter}
      />
    </DataGridContainer>
  );
}
